import { useState, useCallback, useEffect } from 'react';
import type { ContainerStatus, ContainerDashboardStats, ContainerAction } from '../types';
import {
  fetchDashboardStats,
  fetchContainers,
  startContainer,
  stopContainer,
  restartContainer,
} from '../api/containerApi';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

export interface UseContainerDashboardReturn {
  stats: ContainerDashboardStats | null;
  containers: ContainerStatus[];
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
  refresh: () => Promise<void>;
  performAction: (containerId: string, action: ContainerAction) => Promise<void>;
  bulkAction: (action: ContainerAction, containerIds: string[]) => Promise<void>;
}

export const useContainerDashboard = (
  authFetch: AuthFetch
): UseContainerDashboardReturn => {
  const [stats, setStats] = useState<ContainerDashboardStats | null>(null);
  const [containers, setContainers] = useState<ContainerStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [statsData, containersData] = await Promise.all([
        fetchDashboardStats(authFetch),
        fetchContainers(authFetch),
      ]);
      setStats(statsData);
      setContainers(containersData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch container data');
    } finally {
      setIsLoading(false);
    }
  }, [authFetch]);

  const performAction = useCallback(
    async (containerId: string, action: ContainerAction) => {
      setError(null);
      try {
        const actionMap = { start: startContainer, stop: stopContainer, restart: restartContainer };
        await actionMap[action](authFetch, containerId);
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : `Failed to ${action} container`);
      }
    },
    [authFetch, refresh]
  );

  const bulkAction = useCallback(
    async (action: ContainerAction, containerIds: string[]) => {
      setError(null);
      try {
        const actionMap = { start: startContainer, stop: stopContainer, restart: restartContainer };
        const fn = actionMap[action];
        for (const id of containerIds) {
          await fn(authFetch, id);
        }
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : `Bulk ${action} failed`);
      }
    },
    [authFetch, refresh]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { stats, containers, isLoading, error, clearError, refresh, performAction, bulkAction };
};
